"use client"
import React from "react"
import { Clock, Copy, Edit, Globe, Image, Lock, MoreHorizontal, Trash2 } from "lucide-react"

import { Button } from "../ui/button"
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
} from "../ui/dropdown-menu"
import { MetaChip, TagChip, formatDate, updatedAtOf } from "../dashboard/ResourceBits"
import useMagicHover from "../../hooks/useMagicHover"
import { sectionsOf, outputFormatOf } from "./lib/avatarMapper"

/** Globe = visible to everyone, Lock = private to this account. */
const VisibilityBadge = ({ isPublic }) => (
  <span
    title={isPublic ? "Public" : "Private"}
    aria-label={isPublic ? "Public avatar" : "Private avatar"}
    className="absolute right-2.5 top-2.5 flex h-8 w-8 items-center justify-center rounded-full border border-white/15 bg-black/60 backdrop-blur-sm"
  >
    {isPublic ? <Globe className="h-4 w-4 text-white" /> : <Lock className="h-4 w-4 text-white" />}
  </span>
);

/** Shared actions menu — was duplicated across the grid and list variants. */
const AvatarCardMenu = ({ template, onSelect, onDelete, onCopyId, className = "" }) => (
  <DropdownMenu>
    <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
      <Button variant="ghost" size="sm" className={className}>
        <MoreHorizontal className="w-4 h-4 text-gray-400" />
      </Button>
    </DropdownMenuTrigger>
    <DropdownMenuContent align="end" className="bg-gray-900 border border-white/40">
      <DropdownMenuItem
        onClick={(e) => {
          e.stopPropagation();
          onSelect(template);
        }}
        className="text-white hover:bg-white/10 cursor-pointer"
      >
        <Edit className="w-4 h-4 mr-2" />
        Edit
      </DropdownMenuItem>
      <DropdownMenuItem
        onClick={(e) => {
          e.stopPropagation();
          onCopyId(template.id);
        }}
        className="text-white hover:bg-white/10 cursor-pointer"
      >
        <Copy className="w-4 h-4 mr-2" />
        Copy ID
      </DropdownMenuItem>
      {!template.isDefault && (
        <DropdownMenuItem
          onClick={(e) => {
            e.stopPropagation();
            onDelete(template.id);
          }}
          className="text-red-400 hover:bg-red-900/60 hover:text-red-300"
        >
          <Trash2 className="w-4 h-4 mr-2" />
          Delete
        </DropdownMenuItem>
      )}
    </DropdownMenuContent>
  </DropdownMenu>
);

const AvatarCardComponent = ({
  template,
  viewMode,
  onSelect,
  onDelete,
  onCopyId
}, ref) => {
  const sections = sectionsOf(template);
  const language = (template.language || "es").toUpperCase();
  const format = outputFormatOf(template);
  const tags = template.tags || [];

  if (viewMode === "grid") {
    // Misma ficha que en Agents, por el mismo motivo: en móvil esto se lee
    // como una biblioteca de portadas —tres por fila, la imagen mandando— y
    // no como una lista de tarjetas con su descripción y sus etiquetas, que a
    // 110px de ancho no caben. De `md` para arriba vuelve la tarjeta completa.
    //
    // Aquí el menú ⋯ sí desaparece: Borrar, Duplicar y Copiar ID viven dentro
    // del avatar, que es de donde nunca se movieron.
    const subtitle = [
      `${sections} ${sections === 1 ? "section" : "sections"}`,
      language,
    ].filter(Boolean).join(" · ");

    return (
      <>
        <div
          ref={ref}
          role="button"
          tabIndex={0}
          onClick={() => onSelect(template)}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onSelect(template); }
          }}
          className="group cursor-pointer md:hidden focus-visible:outline-none"
        >
          <div className="relative aspect-square w-full overflow-hidden rounded-lg bg-black/40 ring-1 ring-inset ring-white/10 group-focus-visible:ring-2 group-focus-visible:ring-violet-400">
            {template.avatar ? (
              <img
                src={template.avatar}
                alt=""
                className="h-full w-full object-cover"
                loading="lazy"
              />
            ) : (
              <div className="flex h-full w-full items-center justify-center">
                <Image className="h-8 w-8 text-gray-600" />
              </div>
            )}
            <VisibilityBadge isPublic={template.isPublic} />
          </div>
          <span className="mt-2 line-clamp-2 block text-[13px] font-medium leading-snug text-white">
            {template.name}
          </span>
          <span className="mt-0.5 block truncate text-[12px] text-gray-400">{subtitle}</span>
        </div>

        <div
          onClick={() => onSelect(template)}
          className="group relative hidden cursor-pointer flex-col overflow-hidden rounded-2xl border border-white/10 bg-white/[0.03] transition-all duration-300 hover:border-pink-400/50 hover:shadow-lg hover:shadow-pink-500/10 md:flex"
        >
          {/* Cover image.

              Square, like the image itself: what the user framed in ImageCropper
              lands here edge to edge, with nothing cropped and no bars to fill. */}
          <div className="p-3.5 pb-0">
            <div className="relative aspect-square w-full overflow-hidden rounded-lg border border-white/25 bg-black/40 shadow-[inset_0_0_0_1px_rgba(0,0,0,0.6)]">
              {template.avatar ? (
                <img
                  src={template.avatar}
                  alt={template.name}
                  className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-[1.03]"
                  loading="lazy"
                />
              ) : (
                <div className="flex h-full w-full items-center justify-center">
                  <Image className="h-10 w-10 text-gray-600" />
                </div>
              )}
              <VisibilityBadge isPublic={template.isPublic} />
            </div>
          </div>

          {/* Body */}
          <div className="flex flex-1 flex-col p-4 pt-3">
            <div className="flex items-start justify-between gap-2">
              <div className="flex min-w-0 items-center gap-2">
                <h3 className="truncate text-base font-semibold text-white">{template.name}</h3>
                {template.isDefault && (
                  <span className="flex-shrink-0 rounded-full bg-blue-600/20 px-2 py-0.5 text-[10px] text-blue-400">
                    Default
                  </span>
                )}
              </div>
              <AvatarCardMenu
                template={template}
                onSelect={onSelect}
                onDelete={onDelete}
                onCopyId={onCopyId}
                className="-mt-1 flex-shrink-0 opacity-100 transition-opacity [@media(hover:hover)]:opacity-0 [@media(hover:hover)]:group-hover:opacity-100 [@media(hover:hover)]:group-focus-within:opacity-100"
              />
            </div>

            <p className="mt-1 line-clamp-2 text-sm leading-snug text-gray-400">
              {template.description || "No description provided"}
            </p>

            {/* Structural facts */}
            <div className="mt-3 flex flex-wrap gap-1.5">
              <MetaChip>{sections} {sections === 1 ? "section" : "sections"}</MetaChip>
              <MetaChip>{language}</MetaChip>
              <MetaChip>{format}</MetaChip>
            </div>

            {/* Tags */}
            {tags.length > 0 && (
              <div className="mt-2 flex flex-wrap gap-1.5">
                {tags.slice(0, 3).map((tag, idx) => (
                  <TagChip key={idx}>{tag}</TagChip>
                ))}
                {tags.length > 3 && (
                  <span className="px-1 py-0.5 text-[11px] text-gray-500">+{tags.length - 3}</span>
                )}
              </div>
            )}

            {/* Footer */}
            <div className="mt-auto flex items-center justify-between border-t border-white/10 pt-3 text-xs text-gray-500">
              <span className="flex items-center gap-1.5">
                <Clock className="h-3.5 w-3.5" />
                Updated {formatDate(updatedAtOf(template))}
              </span>
            </div>
          </div>
        </div>
      </>
    );
  }

  // List row
  return (
    <div
      ref={ref}
      onClick={() => onSelect(template)}
      className="group relative flex cursor-pointer items-center gap-4 rounded-xl border border-white/10 bg-white/[0.03] p-3 transition-all duration-300 hover:border-pink-400/50 hover:shadow-lg hover:shadow-pink-500/10"
    >
      <div className="relative h-14 w-14 flex-shrink-0 overflow-hidden rounded-xl border border-white/10 bg-black/30">
        {template.avatar ? (
          <img src={template.avatar} alt={template.name} className="h-full w-full object-cover" loading="lazy" />
        ) : (
          <div className="flex h-full w-full items-center justify-center">
            <Image className="h-6 w-6 text-gray-600" />
          </div>
        )}
      </div>

      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <h3 className="truncate font-medium text-white">{template.name}</h3>
          {template.isPublic ? (
            <Globe className="h-3.5 w-3.5 flex-shrink-0 text-gray-400" aria-label="Public" />
          ) : (
            <Lock className="h-3.5 w-3.5 flex-shrink-0 text-gray-400" aria-label="Private" />
          )}
          {template.isDefault && (
            <span className="flex-shrink-0 rounded-full bg-blue-600/20 px-2 py-0.5 text-[10px] text-blue-400">
              Default
            </span>
          )}
        </div>
        <p className="truncate text-sm text-gray-400">
          {template.description || "No description provided"}
        </p>
      </div>

      <div className="hidden flex-shrink-0 items-center gap-1.5 md:flex">
        <MetaChip>{sections} {sections === 1 ? "section" : "sections"}</MetaChip>
        <MetaChip>{language}</MetaChip>
        <MetaChip>{format}</MetaChip>
      </div>

      <span className="hidden flex-shrink-0 items-center gap-1.5 text-xs text-gray-500 lg:flex">
        <Clock className="h-3.5 w-3.5" />
        {formatDate(updatedAtOf(template))}
      </span>

      <AvatarCardMenu
        template={template}
        onSelect={onSelect}
        onDelete={onDelete}
        onCopyId={onCopyId}
        className="flex-shrink-0"
      />
    </div>
  );
};

const AvatarCard = React.memo(React.forwardRef(AvatarCardComponent));
AvatarCard.displayName = 'AvatarCard';

export default AvatarCard;

/** Wraps a card in the tilt/glow hover effect used across the dashboard. */
export function MagicHoverWrapper({ children }) {
  const cardRef = useMagicHover({
    enableTilt: true,
    glowColor: "109, 40, 217",
  });

  if (!React.isValidElement(children)) {
    return children;
  }

  return React.cloneElement(children, {
    ref: cardRef,
    className: `${children.props.className || ""} transform-gpu will-change-transform`,
  });
}
